import React, { useState, useEffect } from 'react';
import { Handle, Position } from '@xyflow/react';
import { Popconfirm, Card, Select, Input, Button, Space, Typography, message } from 'antd';
import { DeleteOutlined, ReloadOutlined } from '@ant-design/icons';
import NodeInputHandler from '../common/NodeInputHandler';
import { adminInstance } from '../api/axiosConfig';

const { Option } = Select;
const { Text } = Typography;

const UnsplashNode = ({ id, data, isConnectable }) => {
  const [nodeName, setNodeName] = useState(data.nodeName || `Unsplash Image ${id}`);
  const [searchTerm, setSearchTerm] = useState(data.searchTerm || '');
  const [imageSize, setImageSize] = useState(data.imageSize || 'regular');
  const [orientation, setOrientation] = useState(data.orientation || 'all');
  const [randomResult, setRandomResult] = useState(data.randomResult || false);
  const [loading, setLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(data.previewUrl || '');

  // Update node data when any field changes
  const updateNodeData = (updates) => {
    const newData = { ...data, ...updates };
    data.updateNodeData(id, newData);
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      message.warning('Please enter a search term');
      return;
    }

    setLoading(true);
    try {
      const response = await adminInstance.post('/unsplash/search', {
        searchTerm,
        orientation,
        randomResult,
        imageSize
      });

      console.log('Unsplash search response:', response.data); // For debugging

      if (response.data.url) {
        setPreviewUrl(response.data.thumb); // Use thumbnail for preview
        updateNodeData({ 
          previewUrl: response.data.thumb,
          imageUrl: response.data.url, // Store the full resolution URL
          searchTerm,
          imageSize,
          orientation,
          randomResult
        });
        message.success('Image fetched successfully');
      } else {
        message.warning('No images found for this search term');
        setPreviewUrl('');
      }
    } catch (error) {
      console.error('Error fetching image:', error);
      message.error('Failed to fetch image: ' + (error.response?.data?.message || error.message));
      setPreviewUrl('');
    } finally {
      setLoading(false);
    }
  };

  const handleNameChange = (e) => {
    const newName = e.target.value;
    setNodeName(newName);
    updateNodeData({ nodeName: newName });
  };

  const handleSearchTermChange = (e) => {
    const newTerm = e.target.value;
    setSearchTerm(newTerm);
    updateNodeData({ searchTerm: newTerm });
  };

  const handleSizeChange = (value) => {
    setImageSize(value);
    updateNodeData({ imageSize: value });
  };

  const handleOrientationChange = (value) => {
    setOrientation(value);
    updateNodeData({ orientation: value });
  };

  return (
    <Card 
      title="Unsplash Image"
      style={{ width: 300 }}
      extra={      <Popconfirm
        title="Delete Node"
        description="Are you sure you want to delete this node?"
        onConfirm={() => data.onDelete(id)}
        okText="Delete"
        cancelText="Cancel"
        okButtonProps={{ danger: true }} > 
        <Button icon={<DeleteOutlined />} /> </Popconfirm>}
    >
      <Handle type="target" position={Position.Left} isConnectable={isConnectable} />

      <Input
        placeholder="Node Name"
        value={nodeName}
        onChange={handleNameChange}
        style={{ marginBottom: 8 }}
        className="nodrag"
      />

      <Input
        placeholder="Search term"
        value={searchTerm}
        onChange={handleSearchTermChange}
        style={{ marginBottom: 8 }}
        className="nodrag"
      />

      <NodeInputHandler 
        nodeId={id} 
        onInsert={(tag) => {
          setSearchTerm(prev => prev + tag);
          updateNodeData({ searchTerm: searchTerm + tag });
        }}
      />

      <Select
        style={{ width: '100%', marginBottom: 8 }}
        value={imageSize}
        onChange={handleSizeChange}
        className="nodrag"
      >
        <Option value="raw">Raw</Option>
        <Option value="full">Full</Option>
        <Option value="regular">Regular</Option>
        <Option value="small">Small</Option>
      </Select>

      <Select
        style={{ width: '100%', marginBottom: 8 }}
        value={orientation}
        onChange={handleOrientationChange}
        className="nodrag"
      >
        <Option value="all">Any Orientation</Option>
        <Option value="landscape">Landscape</Option>
        <Option value="portrait">Portrait</Option>
        <Option value="squarish">Square</Option>
      </Select>

      <Space style={{ marginBottom: 8, width: '100%', justifyContent: 'space-between' }}>
        <Text>Random Result:</Text>
        <Select
          value={randomResult}
          onChange={(value) => {
            setRandomResult(value);
            updateNodeData({ randomResult: value });
          }}
          style={{ width: 100 }}
          className="nodrag"
        >
          <Option value={false}>No</Option>
          <Option value={true}>Yes</Option>
        </Select>
      </Space>

      <div style={{ marginBottom: 8 }}>
        <Button 
          type="primary" 
          onClick={handleSearch} 
          loading={loading}
          icon={<ReloadOutlined />}
          style={{ width: '100%' }}
        >
          Fetch Image
        </Button>
      </div>

      {previewUrl && (
        <div style={{ marginBottom: 8 }}>
          <img 
            src={previewUrl} 
            alt="Preview" 
            style={{ 
              width: '100%', 
              height: 'auto', 
              borderRadius: 4,
              display: 'block',
              marginBottom: 4
            }}
          />
          <Text type="secondary" style={{ fontSize: 12 }}>Preview of selected image</Text>
        </div>
      )}

      <Handle type="source" position={Position.Right} id="a" isConnectable={isConnectable} />
    </Card>
  );
};

export default UnsplashNode;